/**
 * 提现账户动态表单工具
 * 表单结构完全由后台 getWithdrawConfig 返回的 withdrawalInfo 决定。
 */

function normalizeToken(value) {
  return String(value === undefined || value === null ? '' : value)
    .toLowerCase()
    .replace(/[\s_-]+/g, '')
}

export function isSameToken(a, b) {
  const na = normalizeToken(a)
  return na !== '' && na === normalizeToken(b)
}

/** 解析 account.withdraw_info 兼容对象 / JSON 字符串两种形式 */
export function parseWithdrawInfo(raw) {
  if (!raw) {
    return {}
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch (e) {
      return {}
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw
  }
  return {}
}

/** getWithdrawConfig 的配置主体，兼容服务端 data（base64 解码后）与 sdata 两种返回 */
export function resolveWithdrawConfig(res) {
  if (!res) {
    return {}
  }
  const isPlainObject = value => value && typeof value === 'object' && !Array.isArray(value)
  if (isPlainObject(res.data) && Object.keys(res.data).length) {
    return res.data
  }
  if (isPlainObject(res.sdata)) {
    return res.sdata
  }
  return {}
}

function optionTitle(option) {
  if (option === undefined || option === null) {
    return ''
  }
  if (typeof option !== 'object') {
    return String(option)
  }
  if (Array.isArray(option)) {
    return option[1] !== undefined ? option[1] : option[0]
  }
  const keys = ['title', 'label', 'name', 'text', 'value']
  for (let i = 0; i < keys.length; i++) {
    if (option[keys[i]] !== undefined && option[keys[i]] !== null) {
      return option[keys[i]]
    }
  }
  return ''
}

function normalizeOption(option, fallbackKey) {
  if (Array.isArray(option)) {
    return { title: optionTitle(option), key: option[0] !== undefined ? option[0] : fallbackKey }
  }
  if (option && typeof option === 'object') {
    const result = Object.assign({}, option, { title: optionTitle(option) })
    if (result.key === undefined) {
      if (option.value !== undefined) {
        result.key = option.value
      } else if (fallbackKey !== undefined) {
        result.key = fallbackKey
      }
    }
    if (Array.isArray(option.children)) {
      result.children = option.children.map(normalizeField)
    }
    return result
  }
  return {
    title: optionTitle(option),
    key: fallbackKey !== undefined ? fallbackKey : option
  }
}

function normalizeOptions(options) {
  if (typeof options === 'string') {
    try {
      options = JSON.parse(options)
    } catch (e) {
      options = []
    }
  }
  if (Array.isArray(options)) {
    return options.map(option => normalizeOption(option))
  }
  if (options && typeof options === 'object') {
    // 兼容 {"bankCode":"Bank name"} 这种 key:value 对象
    return Object.keys(options).map(key => normalizeOption(options[key], key))
  }
  return []
}

function normalizeField(field) {
  if (!field || typeof field !== 'object') {
    return field
  }
  const normalized = Object.assign({}, field)
  normalized.options = normalizeOptions(field.options)
  return normalized
}

export function resolveWithdrawFields(config) {
  const raw = config && config.withdrawalInfo
  let fields = raw
  if (typeof fields === 'string') {
    try {
      fields = JSON.parse(fields)
    } catch (e) {
      fields = []
    }
  }
  if (!Array.isArray(fields)) {
    return []
  }
  return fields.map(normalizeField)
}

export function findOption(field, value) {
  if (!field || !Array.isArray(field.options) || value === undefined || value === null || value === '') {
    return null
  }
  return field.options.find(option => {
    if (!option) {
      return false
    }
    return isSameToken(option.title, value) || (option.key && isSameToken(option.key, value))
  }) || null
}

/**
 * 当前应该显示（也就是应该校验、提交）的字段，按配置顺序拍平。
 * 顶层字段全部渲染；只有带 children 的选项选中后，才追加子字段。
 */
export function flattenVisibleFields(fields, form, depth = 0) {
  if (!Array.isArray(fields)) {
    return []
  }
  const values = form || {}
  const result = []

  fields.forEach(field => {
    if (!field || !field.key) {
      return
    }
    result.push(Object.assign({}, field, { depth }))
    if (field.type === 'select') {
      const selected = findOption(field, values[field.key])
      if (selected && Array.isArray(selected.children) && selected.children.length) {
        result.push.apply(result, flattenVisibleFields(selected.children, values, depth + 1))
      }
    }
  })

  return result
}

function hasValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

/** 初始化表单：补齐所有字段 key，并回填 account.withdraw_info */
export function buildWithdrawForm(fields, info) {
  const form = {}
  const initFields = list => {
    ;(list || []).forEach(field => {
      if (!field || !field.key) {
        return
      }
      if (form[field.key] === undefined) {
        form[field.key] = ''
      }
      if (field.type === 'select') {
        ;(field.options || []).forEach(option => {
          if (option && Array.isArray(option.children)) {
            initFields(option.children)
          }
        })
      }
    })
  }
  initFields(fields)

  const source = info || {}
  Object.keys(source).forEach(key => {
    if (hasValue(source[key])) {
      form[key] = source[key]
    }
  })

  // select 存的是选项 title；回填时统一成配置里的 title，老数据只有 children 值时反推选项
  const normalizeSelects = list => {
    ;(list || []).forEach(field => {
      if (!field || field.type !== 'select') {
        return
      }
      const options = Array.isArray(field.options) ? field.options : []
      let matched = findOption(field, form[field.key])
      if (!matched) {
        matched = options.find(option => {
          if (!option || !Array.isArray(option.children)) {
            return false
          }
          return option.children.some(child => child && hasValue(form[child.key]))
        }) || null
      }
      // 没有历史值/子字段值时，默认回填第一个选项
      if (!matched && !hasValue(form[field.key])) {
        matched = options.find(option => option && hasValue(option.title)) || null
      }
      if (matched) {
        form[field.key] = matched.title
        if (Array.isArray(matched.children)) {
          normalizeSelects(matched.children)
        }
      }
    })
  }
  normalizeSelects(fields)

  return form
}

/** 只取当前可见且有值的字段 */
export function pickWithdrawInfo(fields, form) {
  const values = form || {}
  const info = {}
  flattenVisibleFields(fields, values).forEach(field => {
    const value = values[field.key]
    if (hasValue(value)) {
      info[field.key] = typeof value === 'string' ? value.trim() : value
    }
  })
  return info
}

/**
 * 必填 + 正则校验。
 * messages.required(field) / messages.invalid(field) 用于返回当前语言的兜底提示。
 */
export function validateWithdrawForm(fields, form, messages = {}) {
  const values = form || {}
  const requiredMessage = typeof messages.required === 'function'
    ? messages.required
    : field => `${field.title} required`
  const invalidMessage = typeof messages.invalid === 'function'
    ? messages.invalid
    : field => `${field.title} invalid`

  const visibleFields = flattenVisibleFields(fields, values)
  for (let i = 0; i < visibleFields.length; i++) {
    const field = visibleFields[i]
    const value = values[field.key]
    if (!hasValue(value)) {
      if (field.required) {
        return requiredMessage(field)
      }
      continue
    }

    const pattern = field.format && field.format.pattern
    if (!pattern) {
      continue
    }
    let reg = null
    try {
      reg = new RegExp(pattern)
    } catch (e) {
      reg = null
    }
    if (reg && !reg.test(String(value).trim())) {
      return (field.format && field.format.message) || invalidMessage(field)
    }
  }
  return ''
}

/** 展示用：按配置顺序列出已保存的 key:value（含选中选项的 children） */
export function describeWithdrawInfo(fields, info) {
  const source = info || {}
  const rows = []
  const used = {}

  const walk = list => {
    ;(list || []).forEach(field => {
      if (!field || !field.key) {
        return
      }
      if (hasValue(source[field.key])) {
        rows.push({ key: field.key, title: field.title || field.key, value: source[field.key] })
        used[field.key] = true
      }
      if (field.type === 'select') {
        const selected = findOption(field, source[field.key])
        if (selected && Array.isArray(selected.children)) {
          walk(selected.children)
        }
      }
    })
  }
  walk(fields)

  // 配置里已经没有的历史字段（如老银行卡的 accountNumber）
  Object.keys(source).forEach(key => {
    if (used[key] || !hasValue(source[key])) {
      return
    }
    rows.push({ key, title: key, value: source[key] })
  })

  return rows
}

/** setBank 的兼容字段：能从提现信息中取到就带上，取不到传空串（由后端处理） */
export function pickCompatBankFields(info) {
  const source = info || {}
  const pick = keys => {
    for (let i = 0; i < keys.length; i++) {
      const value = source[keys[i]]
      if (hasValue(value)) {
        return String(value).trim()
      }
    }
    return ''
  }
  return {
    bankid: pick(['bankid', 'bank_id', 'accountNumber', 'account_number', 'cardNo', 'card_no', 'account', 'card']),
    bank: pick(['bank', 'bankName', 'bank_name', 'bankCode', 'bank_code'])
  }
}
